import { LitElement, html, css, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { readUrlParams } from "./wire";
import type { Session } from "./sign-in";
import { cursors, palette } from "./theme";
import { artUrl } from "./art-url";
import { frameOfManaCost } from "./card-face";
import { playSound } from "./sounds";
import { renderTopNav, topNavLinks, topNavStyles } from "./top-nav";
import {
  DeckEndpoints, EndpointError,
  type Card, type Check, type Deck, type DeckLine, type DeckSummary, type DeckView, type FormatName,
} from "./deck-editor/api";
import {
  COLORS, GROUP_LABEL, TYPE_GROUPS, artColumns, byName, canLead, commanderIdentity, copiesIn, costSymbols, countIn,
  deckSize, flagsByName, inZone, manaCurve, pipColor, releaseName, savedLines, sizeKept, sizeTarget, typeGroup, verdict,
  withCard, withCommander, withoutCommander, type CardFlag, type Color, type RoomRules, type TypeGroup,
} from "./deck-editor/deck";

// Card search on the left; the deck in the middle, grouped by type with a mana curve, or as card art
// by mana value (the List / Art view, kept per player); the commanders and the checklist on the right.

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const icon = (path: string): TemplateResult => html`<svg class="icon" viewBox="0 0 16 16" aria-hidden="true"><path d=${path}></path></svg>`;
const uploadIcon = icon("M8 10.5V2.5M4.75 5.75 8 2.5l3.25 3.25M2.5 10.5v3h11v-3");
const downloadIcon = icon("M8 2.5v8M4.75 7.25 8 10.5l3.25-3.25M2.5 10.5v3h11v-3");

@customElement("deck-editor")
export class DeckEditor extends LitElement {
  // The signed-in player's, from the page that opens this one (app.ts).
  @property({ attribute: false }) session: Session | null = null;
  // What the tab bar says this player is signed in as.
  @property({ attribute: false }) displayName: string | null = null;
  // Shows the tab bar's Admin tab.
  @property({ attribute: false }) admin = false;
  // The tab bar's online count; null until the room list first loads.
  @property({ attribute: false }) online: number | null = null;
  @state() private loadError: string | null = null;
  @state() private decks: DeckSummary[] = [];
  @state() private decksListed = false;
  // The deck as last saved, with the Worker's checklist.
  @state() private deck: Deck | null = null;
  // Its lines as edited, ahead of the save.
  @state() private lines: DeckLine[] = [];
  @state() private saveState: "saved" | "saving" | "failed" = "saved";
  @state() private notice: string | null = null;
  @state() private view: DeckView = "list";
  @state() private rules: RoomRules = new URLSearchParams(location.search).get("rules") === "strict" ? "Strict" : "Casual";

  @state() private text = "";
  @state() private types: TypeGroup[] = [];
  @state() private colors: Color[] = [];
  @state() private withinIdentity = true;
  @state() private playableOnly = true;
  @state() private commandersOnly = false;
  @state() private results: Card[] = [];
  @state() private hasMore = false;
  @state() private page = 0;
  @state() private searchError: string | null = null;

  @state() private hovered: Card | null = null;
  @state() private focused: string | null = null;
  @state() private dialog: "import" | "export" | null = null;
  @state() private importName = "Imported deck";
  @state() private importText = "";
  @state() private importLink = "";
  @state() private importing = false;
  @state() private importError: string | null = null;
  @state() private importUnknown: string[] = [];
  @state() private exportText = "";
  @state() private exportFileName = "deck.deck";

  private endpoints: DeckEndpoints | null = null;
  private readonly release = releaseName(location.pathname);
  private readonly game = readUrlParams().game;
  private searchTimer: number | null = null;
  private searchRun = 0;
  private saveQueued = false;
  private saveRun: Promise<void> | null = null;

  static styles = [palette, cursors, topNavStyles, css`
    :host { display: block; min-height: 100vh; color: var(--text); font-family: var(--font-ui); font-size: 14px; line-height: 1.5; }
    * { box-sizing: border-box; }
    input, select, textarea, button { font: inherit; }
    input[type="text"], input[type="search"], input[type="url"], select, textarea {
      padding: 6px 10px; border-radius: 3.5px; border: 1px solid #000; background: var(--input); color: #bbb;
    }
    input::placeholder, textarea::placeholder { color: var(--text-faint); }
    select {
      padding-right: 30px; cursor: var(--cursor-pointer, pointer); appearance: none; -webkit-appearance: none;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23bbbbbb' stroke-width='1.5'/%3E%3C/svg%3E") no-repeat right 10px center, var(--input);
    }
    input:disabled, select:disabled { opacity: 0.55; cursor: var(--cursor-default, not-allowed); }
    input[type="checkbox"] { accent-color: var(--brand-hi); width: 15px; height: 15px; margin: 0; flex: none; }
    input:focus-visible, select:focus-visible, textarea:focus-visible, button:focus-visible, a:focus-visible, label.btn:focus-within {
      outline: 2px solid var(--lavender); outline-offset: 2px;
    }
    a { color: var(--lavender); text-decoration: none; }
    a:hover { text-decoration: underline; }
    b { color: var(--text-strong); font-weight: 600; }
    .icon { width: 15px; height: 15px; flex: none; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }

    /* Purple for the thing to do next, charcoal for the rest, lavender text for what you can do to the deck. */
    .btn {
      font-size: 14px; font-weight: 500; line-height: 1.2; padding: 7px 14px; cursor: var(--cursor-pointer, pointer); border-radius: 5px; border: 0; white-space: nowrap;
      color: var(--text); background: var(--surface-3); transition: background 150ms, color 150ms;
    }
    .btn:hover:not(:disabled) { background: #3d3d3d; }
    .btn:disabled { opacity: 0.45; cursor: var(--cursor-default, not-allowed); }
    .btn.primary { font-weight: 600; color: var(--text-strong); background: var(--brand); }
    .btn.primary:hover:not(:disabled) { background: var(--brand-hi); }
    .btn.small { font-size: 13px; padding: 4px 10px; }
    .btn.ghost { background: none; }
    .btn.ghost:hover:not(:disabled) { background: var(--surface-3); }
    .action {
      display: inline-flex; align-items: center; gap: 6px; padding: 4px 0; border: 0; background: none; cursor: var(--cursor-pointer, pointer);
      font-size: 14px; font-weight: 500; line-height: 1.2; white-space: nowrap; color: var(--lavender);
    }
    .action:hover:not(:disabled) { text-decoration: underline; }
    .action.danger { color: var(--bad); }

    .pip {
      font-size: 10.5px; font-weight: 700; line-height: 1; min-width: 17px; height: 17px; padding: 0 4px; display: inline-grid; place-items: center;
      border-radius: 9px; background: var(--mana-c); color: #111; box-shadow: -1px 1px 0 #000;
    }
    .pip.W { background: var(--mana-w); }
    .pip.U { background: var(--mana-u); }
    .pip.B { background: var(--mana-b); }
    .pip.R { background: var(--mana-r); }
    .pip.G { background: var(--mana-g); }
    .pip.C { background: var(--mana-c); }
    .cost { display: inline-flex; gap: 2px; vertical-align: middle; flex: none; }
    .cost .pip { font-size: 10px; min-width: 16px; height: 16px; padding: 0 3px; border-radius: 8px; }
    .flag { font-size: 11px; font-weight: 500; line-height: 1; padding: 3px 6px; border-radius: 5.25px; white-space: nowrap; }
    .flag.unplayable, .flag.rule.strict { background: var(--bad-soft); color: #f28b8e; }
    .flag.rule { background: var(--warn-soft); color: var(--warn); }

    .page { display: grid; grid-template-rows: auto auto auto auto 1fr; height: 100vh; background: var(--surface); }
    .page-message { max-width: 560px; margin: 80px auto; padding: 22px 26px; border-radius: 8px; background: var(--surface-2); border: 1px solid var(--border); }
    .page-message p { margin: 0 0 14px; }
    .page-message code { font-family: inherit; color: var(--text-strong); }
    .topbar { display: flex; align-items: center; gap: 8px 12px; flex-wrap: wrap; padding: 10px 20px; background: var(--bg); border-bottom: 1px solid var(--border); }
    .back { margin-right: 12px; white-space: nowrap; }
    .topbar .name { width: 210px; }
    .topbar select.decks { max-width: 210px; }
    .save-state { margin-left: auto; font-size: 13px; color: var(--text-faint); white-space: nowrap; }
    .save-state.failed { color: var(--bad); font-weight: 600; }
    .seg { display: inline-flex; gap: 2px; padding: 2px; border-radius: 5px; background: var(--surface-2); }
    .seg button {
      border: 0; background: none; color: var(--text-dim); padding: 4px 11px; border-radius: 4px; cursor: var(--cursor-pointer, pointer);
      font-size: 13px; font-weight: 500; line-height: 1.3; transition: background 150ms, color 150ms;
    }
    .seg button:hover { color: var(--text-strong); }
    .seg button[aria-pressed="true"] { background: var(--brand); color: var(--text-strong); }
    .seg-label { color: var(--text-dim); }
    .banner { display: flex; align-items: center; gap: 14px; padding: 8px 20px; border-bottom: 1px solid var(--border); }
    .banner.starter { background: var(--lavender-soft); color: var(--text); }
    .banner.notice { background: #3a1416; border-bottom-color: var(--bad); color: #f5c2c4; }
    .banner .btn { margin-left: auto; }
    .banner.notice .btn { color: inherit; }
    .deck-head .banner.starter { margin-top: 12px; padding: 0; gap: 12px; border: 0; background: none; color: rgb(255 255 255 / 0.85); font-size: 13px; }
    .deck-head .banner.starter .btn { margin-left: 0; }
    .kept-checks { margin-top: 2px; }
    .kept-checks summary { padding: 8px 0; cursor: var(--cursor-pointer, pointer); font-size: 13px; color: var(--text-dim); list-style-position: inside; }
    .kept-checks summary:hover { color: var(--text-strong); }

    .cols { display: grid; grid-template-columns: 300px 1fr 300px; min-height: 0; }
    .col { min-height: 0; overflow-y: auto; padding: 16px 18px 60px; scrollbar-width: thin; scrollbar-color: var(--border-strong) transparent; }
    .col.search { border-right: 1px solid var(--border); }
    .col.side { border-left: 1px solid var(--border); }
    .col.deck-col { padding: 16px 24px 60px; }
    h4 { margin: 0 0 10px; font-size: 16px; font-weight: 700; line-height: 1.3; color: var(--text-strong); }
    h4 ~ h4 { margin-top: 26px; }

    .search input[type="search"] { width: 100%; padding: 8px 11px; }
    .chips { display: flex; flex-wrap: wrap; gap: 5px; margin: 12px 0 4px; }
    .chip {
      font-size: 12.5px; font-weight: 500; line-height: 1.2; padding: 4px 9px; border-radius: 5.25px; border: 0; cursor: var(--cursor-pointer, pointer);
      background: var(--surface-3); color: var(--text); transition: background 150ms, color 150ms;
    }
    .chip:hover { background: #3d3d3d; }
    .chip[aria-pressed="true"] { background: var(--brand); color: var(--text-strong); }
    .chips + .chips { margin-top: 10px; gap: 7px; }
    .chip.pip-chip { padding: 0; border-radius: 50%; background: none; opacity: 0.35; transition: opacity 150ms; }
    .chip.pip-chip .pip { min-width: 24px; height: 24px; border-radius: 12px; font-size: 12px; }
    .chip.pip-chip:hover { background: none; opacity: 0.7; }
    .chip.pip-chip[aria-pressed="true"] { background: none; opacity: 1; box-shadow: 0 0 0 2px var(--surface), 0 0 0 3.5px var(--lavender); }
    .toggles { display: flex; flex-direction: column; gap: 5px; margin: 12px 0 14px; }
    .toggles label { cursor: var(--cursor-pointer, pointer); user-select: none; display: flex; gap: 8px; align-items: center; }
    .result-count { font-size: 13px; color: var(--text-dim); padding: 10px 0 4px; border-top: 1px solid var(--border); }
    .results { list-style: none; margin: 0 -8px 10px; padding: 0; }
    .result { display: grid; grid-template-columns: 1fr auto; gap: 1px 8px; align-items: center; padding: 6px 8px; border-radius: 4px; }
    .result:hover { background: var(--surface-2); }
    .rname { font-weight: 600; color: var(--text-strong); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .result.unplayable .rname { color: var(--text-dim); }
    .rtype { font-size: 12.5px; color: var(--text-dim); display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
    .ractions { display: flex; gap: 4px; grid-row: span 2; }
    .ractions .btn { min-width: 30px; padding: 4px 8px; }
    .ractions .btn.add { color: var(--lavender); font-size: 16px; line-height: 1; padding: 3px 9px 4px; }
    .in-deck { color: var(--lavender); }
    .search-error { color: var(--bad); margin-bottom: 8px; }

    /* Moxfield's deck banner: the title on purple, over the art of the deck's commander. */
    .deck-head {
      display: flex; align-items: flex-end; gap: 14px 32px; flex-wrap: wrap; padding: 34px 28px 22px; border-radius: 8px; color: var(--text-strong);
      background:
        linear-gradient(to top, rgb(28 8 42 / 0.6), transparent 55%),
        linear-gradient(100deg, #731fa1 0%, rgb(115 31 161 / 0.92) 30%, rgb(82 21 120 / 0.45) 62%, rgb(44 12 66 / 0.12) 100%),
        var(--art, none) 50% 29% / 120% auto no-repeat,
        #2c0c42;
    }
    .deck-id { flex: 1 1 300px; min-width: 0; }
    .deck-title { font-size: clamp(32px, 3.2vw, 48px); font-weight: 700; line-height: 1.08; overflow-wrap: anywhere; }
    .deck-sub { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 12px; color: rgb(255 255 255 / 0.82); }
    .tag { font-size: 11px; font-weight: 500; line-height: 1; padding: 4px 7px; border-radius: 5.25px; background: var(--tag); color: var(--text-strong); }
    .count-big { font-size: 32px; font-weight: 700; line-height: 1.1; font-variant-numeric: tabular-nums; }
    .count-big.bad { color: var(--warn); }
    .count-big small { font-size: 16px; font-weight: 500; color: rgb(255 255 255 / 0.7); }
    .deck-counts { margin-top: 4px; font-size: 13px; color: rgb(255 255 255 / 0.82); }
    .curve { display: flex; align-items: flex-end; gap: 4px; height: 44px; padding-bottom: 17px; box-sizing: content-box; }
    .curve .bar { width: 14px; min-height: 2px; position: relative; border-radius: 2px 2px 0 0; background: rgb(255 255 255 / 0.78); }
    .curve .bar span { position: absolute; bottom: -17px; left: -3px; right: -3px; text-align: center; font-size: 11px; color: rgb(255 255 255 / 0.7); }
    .deck-actions { display: flex; align-items: center; gap: 18px; flex-wrap: wrap; margin: 12px 2px 18px; }
    .deck-actions .seg { margin-left: auto; }

    .groups { column-width: 260px; column-gap: 32px; }
    .group { break-inside: avoid; margin-bottom: 20px; }
    .group h5 {
      margin: 0 0 4px; padding: 0 6px 4px; display: flex; align-items: baseline; gap: 8px; border-bottom: 1px solid var(--border);
      font-size: 14px; font-weight: 700; color: var(--text-strong);
    }
    .group h5 em { margin-left: auto; font-size: 12.5px; font-weight: 400; font-style: normal; color: var(--text-faint); }
    .row { display: flex; align-items: center; gap: 6px; min-height: 26px; padding: 1px 6px 1px 3px; border-left: 3px solid transparent; border-radius: 0 4px 4px 0; }
    .row:hover { background: var(--surface-2); }
    .row.unplayable { border-left-color: var(--bad); }
    .row.unplayable .nm { color: var(--text-faint); }
    .row.rule { border-left-color: var(--warn); }
    .row.focused { background: var(--lavender-soft); }
    .row .n { width: 20px; flex: none; text-align: right; font-variant-numeric: tabular-nums; }
    .row .nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .steps { display: flex; gap: 3px; width: 47px; flex: none; }
    .step {
      width: 22px; height: 22px; padding: 0; flex: none; border-radius: 4px; border: 0; cursor: var(--cursor-pointer, pointer); font-size: 14px; line-height: 1;
      background: var(--surface-3); color: var(--text); opacity: 0; transition: opacity 120ms, background 150ms;
    }
    .row:hover .step, .tile:hover .step, .step:focus-visible { opacity: 1; }
    .step:hover { background: #3d3d3d; color: var(--text-strong); }
    .empty { color: var(--text-dim); max-width: 34em; }

    .board { display: flex; gap: 10px; align-items: flex-start; overflow-x: auto; padding: 2px 2px 12px; --card-w: 96px; --card-h: 134px; }
    .colm { width: var(--card-w); flex: none; }
    .colm-head { display: flex; align-items: baseline; justify-content: space-between; margin: 0 2px 6px; font-size: 12.5px; color: var(--text-dim); }
    .colm-head b { font-size: 14px; color: var(--text-strong); }
    .stack { display: flex; flex-direction: column; padding-bottom: 8px; }
    .tile { position: relative; border-radius: 5px; transition: transform 160ms var(--ease-out); }
    .tile + .tile { margin-top: calc(var(--card-h) * -0.8); }
    .tile:hover ~ .tile { transform: translateY(calc(var(--card-h) * 0.72)); }
    .tile:hover { z-index: 5; }
    .tile .nm {
      position: absolute; left: 0; right: 0; top: 0; padding: 4px 6px; border-radius: 5px 5px 0 0; pointer-events: none;
      font-size: 10.5px; font-weight: 600; line-height: 1.3; color: var(--text-strong); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      background: rgb(17 17 17 / 0.86);
    }
    .tile .qty, .tile .ribbon { position: absolute; top: 24px; font-size: 11px; font-weight: 600; line-height: 1; padding: 3px 5px; border-radius: 5.25px; }
    .tile .qty { right: 4px; background: var(--surface-2); color: var(--text-strong); box-shadow: 0 1px 4px rgb(0 0 0 / 0.6); }
    .tile .ribbon { left: 4px; }
    .tile.unplayable card-art { filter: grayscale(1) brightness(0.6); }
    .tile.rule { box-shadow: 0 0 0 2px var(--warn); }
    .tile.focused { box-shadow: 0 0 0 2px var(--lavender), 0 0 20px -4px var(--lavender); }
    .tile.unplayable .ribbon { background: var(--bad); color: var(--text-strong); }
    .tile.rule .ribbon { background: var(--warn); color: #111; }
    .tile .tile-steps { position: absolute; right: 4px; bottom: 4px; display: flex; gap: 3px; }
    .art-side { margin-top: 24px; --card-w: 74px; --card-h: 103px; }
    .art-side .row-tiles { display: flex; gap: 6px; flex-wrap: wrap; }
    .art-side .tile + .tile { margin-top: 0; }
    .art-side .tile:hover ~ .tile { transform: none; }
    .art-side .tile .nm { font-size: 9px; }
    .board + .group { margin-top: 24px; }

    .slot { display: flex; gap: 10px; flex-wrap: wrap; --card-w: 136px; --card-h: 190px; margin-bottom: 12px; }
    .slot .cmdr { display: flex; flex-direction: column; gap: 4px; align-items: center; }
    .slot .open {
      width: 136px; height: 190px; padding: 12px; display: grid; place-items: center; text-align: center; border-radius: 6px;
      border: 1px dashed var(--border-strong); background: var(--bg); color: var(--text-faint); font-size: 13px; line-height: 1.4;
    }
    .suggest { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; margin-bottom: 16px; }
    .rules-pick { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 0 0 10px; }
    .verdict {
      display: grid; grid-template-columns: 18px 1fr; gap: 8px; padding: 2px 0 9px; border-bottom: 1px solid var(--border-strong);
      font-size: 14px; font-weight: 600; line-height: 1.45; color: var(--text-strong);
    }
    .verdict::before { font-weight: 700; text-align: center; }
    .verdict.ok::before { content: "✓"; color: var(--good); }
    .verdict.warn::before { content: "!"; color: var(--warn); }
    .verdict.blocked::before { content: "✗"; color: var(--bad); }
    .checks { list-style: none; margin: 0; padding: 0; }
    .check { display: grid; grid-template-columns: 18px 1fr; gap: 1px 8px; padding: 7px 0; border-bottom: 1px solid var(--border); font-size: 13.5px; line-height: 1.45; }
    .check .mark { font-weight: 700; text-align: center; }
    .check.kept .mark { color: var(--good); }
    .check.warned .mark { color: var(--warn); }
    .check.refused .mark { color: var(--bad); }
    .check .detail { grid-column: 2; font-size: 12.5px; color: var(--text-dim); }
    .check .cards { grid-column: 2; display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
    .check .cards button {
      font-size: 12px; line-height: 1.2; padding: 3px 7px; border-radius: 5.25px; border: 0; cursor: var(--cursor-pointer, pointer);
      background: var(--surface-3); color: var(--text); transition: background 150ms;
    }
    .check .cards button:hover { background: var(--tag); color: var(--text-strong); }
    .rules-note { font-size: 12.5px; color: var(--text-dim); margin-top: 14px; }

    .preview {
      position: fixed; left: 320px; top: 110px; z-index: 50; pointer-events: none; width: 260px; display: flex; flex-direction: column; gap: 10px;
      --card-w: 260px; --card-h: 363px; filter: drop-shadow(0 18px 30px rgb(0 0 0 / 0.6));
    }
    .preview-text { padding: 10px 12px; border-radius: 6px; background: var(--surface-2); border: 1px solid var(--border-strong); font-size: 13.5px; }
    .preview-name { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 15px; font-weight: 700; color: var(--text-strong); }
    .preview-type { color: var(--text-dim); margin-top: 2px; }
    .preview-oracle { margin-top: 7px; white-space: pre-wrap; }
    .preview-warning { margin-top: 7px; color: #f28b8e; }

    .scrim { position: fixed; inset: 0; z-index: 200; display: grid; place-items: center; background: rgb(0 0 0 / 0.6); }
    .dialog {
      width: min(92vw, 640px); padding: 18px 22px 20px; display: flex; flex-direction: column; gap: 12px; border-radius: 8px;
      background: var(--surface); border: 1px solid var(--border-strong); box-shadow: 0 20px 60px rgb(0 0 0 / 0.6);
    }
    .dialog h3 { margin: 0; font-size: 18px; font-weight: 700; color: var(--text-strong); }
    .dialog p { margin: 0; color: var(--text-dim); }
    .dialog textarea { width: 100%; height: 46vh; resize: vertical; font-size: 13.5px; line-height: 1.55; white-space: pre; }
    .page-message .row-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .page-message .btn { display: inline-flex; align-items: center; gap: 6px; }
    .dialog .row-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .dialog .grow { flex: 1; }
    .dialog .errors { color: #f5c2c4; max-height: 140px; overflow: auto; }
    .dialog .errors ul { margin: 4px 0 0; padding-left: 20px; }
    .dialog a.btn { text-decoration: none; }

    @media (hover: none) {
      .step { opacity: 1; }
    }
    @media (prefers-reduced-motion: reduce) {
      .tile, .tile:hover ~ .tile { transition: none; }
    }
    @media (max-width: 1080px) {
      .page { height: auto; min-height: 100vh; }
      .cols { grid-template-columns: 1fr; }
      .col { overflow: visible; padding-bottom: 24px; }
      .col.search, .col.side { border: 0; }
      .col.deck-col { padding: 16px 16px 24px; border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); }
      .deck-head { padding: 26px 20px 18px; }
      .preview { display: none; }
    }
  `];

  connectedCallback(): void {
    super.connectedCallback();
    void this.start();
  }

  private get api(): DeckEndpoints {
    if (!this.endpoints) throw new Error("not signed in");
    return this.endpoints;
  }

  private async start(): Promise<void> {
    try {
      if (!this.session) throw new Error("not signed in");
      this.endpoints = new DeckEndpoints(this.session);
      const [decks, view] = await Promise.all([this.api.listDecks(), this.api.readDeckView()]);
      this.decks = decks;
      this.decksListed = true;
      this.view = view;
      const wanted = new URLSearchParams(location.search).get("deck");
      const asked = decks.find((d) => d.deck_id === wanted);
      const first = asked ?? decks.find((d) => !d.starter) ?? decks[0];
      if (first) {
        await this.open(first.deck_id);
        if (wanted && !asked) this.notice = `There's no deck ${wanted} you can see, so here's ${first.name}.`;
      }
      void this.search(0);
    } catch (e) {
      this.loadError = `The decks couldn't be loaded: ${message(e)}`;
    }
  }

  private async open(deckId: string): Promise<void> {
    await this.saveRun;
    try {
      this.show(await this.api.readDeck(deckId));
    } catch (e) {
      this.notice = message(e);
    }
  }

  private show(deck: Deck): void {
    const formatChanged = this.deck?.format !== deck.format;
    this.deck = deck;
    this.lines = deck.lines;
    this.focused = null;
    const params = new URLSearchParams(location.search);
    params.set("deck", deck.deck_id);
    history.replaceState(null, "", `${location.pathname}?${params}`);
    if (formatChanged && this.results.length > 0) this.searchSoon();
  }

  private closeDeck(): void {
    this.deck = null;
    this.lines = [];
    this.focused = null;
    const params = new URLSearchParams(location.search);
    params.delete("deck");
    history.replaceState(null, "", `${location.pathname}?${params}`);
  }

  private async refreshDecks(): Promise<void> {
    try { this.decks = await this.api.listDecks(); } catch (e) { this.notice = message(e); }
  }

  // Edits are the player's own decks only; the page offers none on a starter deck.
  private edit(next: DeckLine[]): void {
    if (!this.deck || this.deck.starter) return;
    const identityBefore = commanderIdentity(this.lines).join("");
    this.lines = next;
    this.queueSave();
    if (commanderIdentity(next).join("") !== identityBefore && this.withinIdentity) this.searchSoon();
  }

  private queueSave(): void {
    this.saveQueued = true;
    this.saveRun ??= this.saveQueuedLines().finally(() => { this.saveRun = null; });
  }

  // One save at a time, each of the lines as they are when it starts; edits made while one is out
  // go in the next.
  private async saveQueuedLines(): Promise<void> {
    this.saveState = "saving";
    try {
      while (this.saveQueued && this.deck) {
        this.saveQueued = false;
        const deckId = this.deck.deck_id;
        const saved = await this.api.replaceDeckLines(deckId, savedLines(this.lines, this.deck.unknown_lines));
        if (this.deck?.deck_id !== deckId) continue;
        this.deck = saved;
        if (!this.saveQueued) this.lines = saved.lines;
      }
      this.saveState = "saved";
    } catch (e) {
      this.saveQueued = false;
      this.saveState = "failed";
      this.notice = `The deck couldn't be saved: ${message(e)}`;
    }
  }

  private async rename(name: string): Promise<void> {
    if (!this.deck || name.trim() === this.deck.name) return;
    await this.saveRun;
    try {
      this.show(await this.api.renameDeck(this.deck.deck_id, name));
      await this.refreshDecks();
    } catch (e) {
      this.notice = message(e);
      this.requestUpdate();
    }
  }

  private async changeFormat(format: FormatName): Promise<void> {
    if (!this.deck || format === this.deck.format) return;
    await this.saveRun;
    try {
      this.show(await this.api.changeDeckFormat(this.deck.deck_id, format));
      await this.refreshDecks();
    } catch (e) {
      this.notice = message(e);
    }
  }

  private async newDeck(): Promise<void> {
    await this.saveRun;
    try {
      const created = await this.api.createDeck("New deck", "Commander");
      await this.refreshDecks();
      this.show(created);
      await this.updateComplete;
      this.renderRoot.querySelector<HTMLInputElement>("input.name")?.select();
    } catch (e) {
      this.notice = message(e);
    }
  }

  private async copyThisDeck(): Promise<void> {
    if (!this.deck) return;
    await this.saveRun;
    try {
      const copy = await this.api.copyDeck(this.deck.deck_id);
      await this.refreshDecks();
      this.show(copy);
    } catch (e) {
      this.notice = message(e);
    }
  }

  private async deleteThisDeck(): Promise<void> {
    const deck = this.deck;
    if (!deck || deck.starter || !confirm(`Delete ${deck.name}? This can't be undone.`)) return;
    await this.saveRun;
    try {
      await this.api.deleteDeck(deck.deck_id);
      await this.refreshDecks();
      const next = this.decks.find((d) => !d.starter) ?? this.decks[0];
      if (next) await this.open(next.deck_id);
      else this.closeDeck();
    } catch (e) {
      this.notice = message(e);
    }
  }

  private async pickView(view: DeckView): Promise<void> {
    if (view === this.view) return;
    this.view = view;
    try { await this.api.saveDeckView(view); } catch (e) { this.notice = `Your deck view couldn't be kept: ${message(e)}`; }
  }

  private searchSoon(): void {
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => { this.searchTimer = null; void this.search(0); }, 180);
  }

  private async search(page: number): Promise<void> {
    const run = ++this.searchRun;
    try {
      const found = await this.api.searchCards({
        text: this.text, types: this.types, colors: this.colorFilter(), playable: this.playableOnly,
        commanders: this.commandersOnly, format: this.deck?.format ?? "Commander", page,
      });
      if (run !== this.searchRun) return;
      this.results = page === 0 ? found.cards : [...this.results, ...found.cards];
      this.page = page;
      this.hasMore = found.has_more;
      this.searchError = null;
    } catch (e) {
      if (run === this.searchRun) this.searchError = message(e);
    }
  }

  // The colors chosen, inside the commanders' identity when that box is ticked; null for any.
  private colorFilter(): string | null {
    const identity = this.withinIdentity && this.deck?.format === "Commander" && inZone(this.lines, "commander").length > 0
      ? commanderIdentity(this.lines) : null;
    const chosen = this.colors.length > 0 ? this.colors : null;
    const within = identity && chosen ? identity.filter((c) => chosen.includes(c)) : identity ?? chosen;
    return within === null ? null : within.join("");
  }

  private toggled<T>(list: T[], item: T): T[] {
    return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
  }

  private focusCard(name: string): void {
    this.focused = name;
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector(`.deck-col [data-card="${CSS.escape(name)}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }

  private async openExport(): Promise<void> {
    if (!this.deck) return;
    await this.saveRun;
    this.dialog = "export";
    this.exportText = "";
    try {
      const exported = await this.api.exportDeck(this.deck.deck_id);
      this.exportText = exported.text;
      this.exportFileName = exported.fileName;
    } catch (e) {
      this.exportText = `# ${message(e)}`;
    }
  }

  // A link can't carry the access token, so the text already fetched is what's saved.
  private downloadExport(): void {
    const url = URL.createObjectURL(new Blob([this.exportText], { type: "text/plain" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = this.exportFileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  private openImport(): void {
    this.dialog = "import";
    this.importError = null;
    this.importUnknown = [];
  }

  private async loadImportFile(e: Event): Promise<void> {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.importText = await file.text();
    this.importName = file.name.replace(/\.(deck|txt)$/, "");
  }

  private async importAsNewDeck(read: () => Promise<Deck>): Promise<void> {
    this.importError = null;
    this.importUnknown = [];
    this.importing = true;
    try {
      const imported = await read();
      this.dialog = null;
      this.importText = "";
      this.importLink = "";
      await this.refreshDecks();
      this.show(imported);
    } catch (e) {
      this.importError = message(e);
      this.importUnknown = e instanceof EndpointError ? e.unknownCards : [];
    } finally {
      this.importing = false;
    }
  }

  private importDeckText(): Promise<void> {
    return this.importAsNewDeck(() => this.api.importDeck(this.importName, this.importText));
  }

  private importArchidektDeck(): Promise<void> {
    return this.importAsNewDeck(() => this.api.importArchidektDeck(this.importLink.trim()));
  }

  // This page, open on no deck in particular, for the tab bar's Decks.
  private decksHref(): string {
    const params = new URLSearchParams();
    const { user } = readUrlParams();
    if (user) params.set("user", user);
    return `deck-editor?${params}`;
  }

  private backHref(): string {
    const params = new URLSearchParams();
    const { user } = readUrlParams();
    if (user) params.set("user", user);
    if (this.game) params.set("game", this.game);
    return `./?${params}`;
  }

  render(): TemplateResult {
    const nav = renderTopNav({ open: "decks", displayName: this.displayName, online: this.online, links: topNavLinks(this.decksHref()), admin: this.admin });
    if (this.loadError) return html`${nav}<div class="page-message">${this.loadError} <a href=${this.backHref()}>Back</a></div>`;
    const deck = this.deck;
    if (!deck && this.decksListed && this.decks.length === 0) return this.renderNoDecks(nav);
    if (!deck) return html`${nav}<div class="page-message">Loading your decks…</div>`;
    return html`
      <div class="page">
        ${nav}
        ${this.renderTopbar(deck)}
        <div></div>
        ${this.notice ? html`
          <div class="banner notice" role="alert">
            <span>${this.notice}</span>
            <button class="btn small" @click=${() => { this.notice = null; }}>Dismiss</button>
          </div>` : html`<div></div>`}
        <div class="cols">
          <section class="col search" aria-label="Card search">${this.renderSearch(deck)}</section>
          <section class="col deck-col" aria-label="Deck">${this.renderDeck(deck)}</section>
          <section class="col side" aria-label="Commanders and checks">${this.renderSide(deck)}</section>
        </div>
      </div>
      ${this.hovered ? this.renderPreview(this.hovered) : nothing}
      ${this.dialog === "import" ? this.renderImport() : nothing}
      ${this.dialog === "export" ? this.renderExport(deck) : nothing}
    `;
  }

  private renderNoDecks(nav: TemplateResult): TemplateResult {
    return html`
      ${nav}
      <div class="page-message">
        <p>You have no decks yet. Start one from scratch, or import a list or an Archidekt link.</p>
        ${this.notice ? html`<p role="alert">${this.notice}</p>` : nothing}
        <div class="row-actions">
          <button class="btn primary" @click=${() => this.newDeck()}>+ New deck</button>
          <button class="btn" @click=${() => this.openImport()}>${uploadIcon}Import deck</button>
          ${this.game ? html`<a href=${this.backHref()}>Back to the lobby</a>` : nothing}
        </div>
      </div>
      ${this.dialog === "import" ? this.renderImport() : nothing}
    `;
  }

  private renderTopbar(deck: Deck): TemplateResult {
    const mine = this.decks.filter((d) => !d.starter);
    const starters = this.decks.filter((d) => d.starter);
    return html`
      <div class="topbar">
        ${this.game ? html`<a class="back" href=${this.backHref()}>← Back to the lobby</a>` : nothing}
        <select class="decks" aria-label="Deck" @change=${(e: Event) => this.open((e.target as HTMLSelectElement).value)}>
          ${mine.length > 0 ? html`<optgroup label="Your decks">
            ${mine.map((d) => html`<option value=${d.deck_id} ?selected=${d.deck_id === deck.deck_id}>${d.name}</option>`)}
          </optgroup>` : nothing}
          ${starters.length > 0 ? html`<optgroup label="Starter decks">
            ${starters.map((d) => html`<option value=${d.deck_id} ?selected=${d.deck_id === deck.deck_id}>★ ${d.name}</option>`)}
          </optgroup>` : nothing}
        </select>
        <input type="text" class="name" aria-label="Deck name" maxlength="60" .value=${deck.name} ?disabled=${deck.starter}
          @change=${(e: Event) => this.rename((e.target as HTMLInputElement).value)}>
        <select aria-label="Format" ?disabled=${deck.starter} @change=${(e: Event) => this.changeFormat((e.target as HTMLSelectElement).value as FormatName)}>
          ${(["Commander", "Standard"] as const).map((f) => html`<option value=${f} ?selected=${deck.format === f}>${f}</option>`)}
        </select>
        <button class="action" @click=${() => this.newDeck()}>+ New deck</button>
        <button class="action" @click=${() => this.openImport()}>${uploadIcon}Import deck</button>
        ${deck.starter ? nothing : html`<button class="action danger" @click=${() => this.deleteThisDeck()}>Delete</button>`}
        <span class="save-state ${this.saveState}" role="status" aria-label="Save state">${
          deck.starter ? "Read-only" : this.saveState === "saving" ? "Saving…" : this.saveState === "failed" ? "Not saved" : "Saved"}</span>
      </div>`;
  }

  private renderSearch(deck: Deck): TemplateResult {
    const commanderDeck = deck.format === "Commander";
    const identity = inZone(this.lines, "commander").length > 0 ? commanderIdentity(this.lines) : null;
    return html`
      <input type="search" aria-label="Search cards" placeholder="Search name, type or text…" .value=${this.text}
        @input=${(e: Event) => { this.text = (e.target as HTMLInputElement).value; this.searchSoon(); }}>
      <div class="chips" role="group" aria-label="Card types">
        ${TYPE_GROUPS.map((t) => html`<button class="chip" aria-pressed=${this.types.includes(t) ? "true" : "false"}
          @click=${() => { this.types = this.toggled(this.types, t); void this.search(0); }}>${t}</button>`)}
      </div>
      <div class="chips" role="group" aria-label="Colors">
        ${COLORS.map((c) => html`<button class="chip pip-chip" title="Color identity inside ${c}" aria-label="Color ${c}"
          aria-pressed=${this.colors.includes(c) ? "true" : "false"}
          @click=${() => { this.colors = this.toggled(this.colors, c); void this.search(0); }}><span class="pip ${c}">${c}</span></button>`)}
      </div>
      <div class="toggles">
        ${commanderDeck ? html`<label><input type="checkbox" .checked=${this.withinIdentity}
            @change=${(e: Event) => { this.withinIdentity = (e.target as HTMLInputElement).checked; void this.search(0); }}>
          <span>Inside the commanders' colors ${identity ? this.renderIdentity(identity) : html`<span class="seg-label">(no commander yet)</span>`}</span></label>` : nothing}
        <label><input type="checkbox" .checked=${this.playableOnly}
            @change=${(e: Event) => { this.playableOnly = (e.target as HTMLInputElement).checked; void this.search(0); }}>
          Only cards playable on ${this.release}</label>
        <label><input type="checkbox" .checked=${this.commandersOnly}
            @change=${(e: Event) => { this.commandersOnly = (e.target as HTMLInputElement).checked; void this.search(0); }}>
          Only cards that can be a commander</label>
      </div>
      ${this.searchError ? html`<div class="search-error" role="alert">${this.searchError}</div>` : nothing}
      <div class="result-count">${this.results.length}${this.hasMore ? "+" : ""} cards</div>
      <ul class="results">
        ${this.results.map((card) => this.renderResult(deck, card))}
      </ul>
      ${this.hasMore ? html`<button class="btn small" @click=${() => this.search(this.page + 1)}>More cards</button>` : nothing}
    `;
  }

  private renderResult(deck: Deck, card: Card): TemplateResult {
    const inDeck = copiesIn(this.lines, card.oracle_id);
    const editable = !deck.starter;
    return html`
      <li class="result ${card.playable ? "" : "unplayable"}" data-card=${card.name}
        @mouseenter=${() => this.hoverCard(card)} @mouseleave=${() => { this.hovered = null; }}>
        <div class="rname">${card.name}</div>
        <div class="ractions">
          ${editable && deck.format === "Commander" && canLead(card) ? html`<button class="btn small ghost" title="Make commander"
            aria-label="Make ${card.name} a commander" @click=${() => this.edit(withCommander(this.lines, card))}>★</button>` : nothing}
          ${editable ? html`<button class="btn small ghost" title="Add to the sideboard" aria-label="Add ${card.name} to the sideboard"
            @click=${() => this.edit(withCard(this.lines, card, "sideboard", 1))}>SB</button>` : nothing}
          <button class="btn small add" title="Add to the main deck" aria-label="Add ${card.name}" ?disabled=${!editable}
            @click=${() => this.edit(withCard(this.lines, card, "main", 1))}>+</button>
        </div>
        <div class="rtype">
          ${this.renderCost(card.mana_cost)} <span>${card.type_line}</span>
          ${card.playable ? nothing : html`<span class="flag unplayable">Not on ${this.release}</span>`}
          ${card.legality === "Legal" ? nothing : html`<span class="flag rule">${card.legality === "NotLegal" ? `Not ${deck.format}` : card.legality}</span>`}
          ${inDeck > 0 ? html`<span class="in-deck">×${inDeck} in deck</span>` : nothing}
        </div>
      </li>`;
  }

  private renderDeck(deck: Deck): TemplateResult {
    const size = deckSize(deck.format, this.lines);
    const curve = manaCurve(this.lines);
    const peak = Math.max(1, ...curve);
    const commanderDeck = deck.format === "Commander";
    const art = this.bannerCard();
    return html`
      <div class="deck-head" style=${art ? `--art: url("${artUrl(art.name)}")` : ""}>
        <div class="deck-id">
          <div class="deck-title">${deck.name}</div>
          <div class="deck-sub">
            <span class="tag">${deck.format}</span>
            ${commanderDeck ? this.renderIdentity(commanderIdentity(this.lines)) : nothing}
            <span>${deck.starter ? "★ Starter deck" : "Your deck"}</span>
          </div>
          ${deck.starter ? html`<div class="banner starter">
            <span>${deck.name} is a starter deck, which nobody can change.</span>
            <button class="btn primary small" @click=${() => this.copyThisDeck()}>Copy to my decks</button>
          </div>` : nothing}
        </div>
        <div>
          <div class="count-big ${sizeKept(deck.format, size) ? "ok" : "bad"}" aria-label="Deck size">${size}<small> / ${sizeTarget(deck.format)}</small></div>
          <div class="deck-counts">${countIn(this.lines, "main")} main${commanderDeck ? `, ${countIn(this.lines, "commander")} commander` : ""}, ${countIn(this.lines, "sideboard")} sideboard</div>
        </div>
        <div class="curve" aria-label="Mana curve" title="Mana curve: nonland main-deck cards">
          ${curve.map((n, mv) => html`<div class="bar" style="height:${(n / peak) * 44}px" title="${n} at ${mv === 7 ? "7+" : mv}"><span>${mv === 7 ? "7+" : mv}</span></div>`)}
        </div>
      </div>
      <div class="deck-actions">
        <button class="action" @click=${() => this.openExport()}>${downloadIcon}Export .deck</button>
        <span class="seg view" role="group" aria-label="Deck view">
          ${(["list", "art"] as const).map((v) => html`
            <button aria-pressed=${this.view === v ? "true" : "false"} @click=${() => this.pickView(v)}>${v === "list" ? "List" : "Art"}</button>`)}
        </span>
      </div>
      ${this.lines.length === 0 && deck.unknown_lines.length === 0
        ? html`<div class="empty">An empty deck. Search on the left and press + to add cards${commanderDeck ? ", or ★ to choose a commander" : ""}.</div>`
        : this.view === "list" ? this.renderList(deck) : this.renderArt(deck)}
    `;
  }

  private renderList(deck: Deck): TemplateResult {
    const flags = flagsByName(deck.checks);
    const main = inZone(this.lines, "main");
    const group = (title: string, lines: DeckLine[], note?: string) => lines.length === 0 ? nothing : html`
      <div class="group" data-group=${title}>
        <h5>${title} (${lines.reduce((n, l) => n + l.count, 0)})${note ? html`<em>${note}</em>` : nothing}</h5>
        ${[...lines].sort(byName).map((l) => this.renderRow(deck, l, flags.get(l.card.name)))}
      </div>`;
    return html`
      <div class="groups">
        ${group(inZone(this.lines, "commander").length > 1 ? "Commanders" : "Commander", inZone(this.lines, "commander"))}
        ${TYPE_GROUPS.map((t) => group(GROUP_LABEL[t], main.filter((l) => typeGroup(l.card) === t)))}
        ${group("Sideboard", inZone(this.lines, "sideboard"), deck.format === "Commander" ? "Not played in Commander" : undefined)}
        ${this.renderUnknownLines(deck)}
      </div>`;
  }

  private renderUnknownLines(deck: Deck): TemplateResult | typeof nothing {
    if (deck.unknown_lines.length === 0) return nothing;
    return html`
      <div class="group">
        <h5>Unknown cards (${deck.unknown_lines.reduce((n, l) => n + l.count, 0)})</h5>
        ${deck.unknown_lines.map((l) => html`<div class="row unplayable"><span class="n">${l.count}</span>
          <span class="nm" title="No card has this oracle id">oracle id ${l.oracle_id} (${l.zone})</span></div>`)}
      </div>`;
  }

  private renderRow(deck: Deck, line: DeckLine, flag: CardFlag | undefined): TemplateResult {
    const { card, zone } = line;
    const editable = !deck.starter;
    return html`
      <div class="row ${flag ?? ""} ${this.focused === card.name ? "focused" : ""}" data-card=${card.name} data-zone=${zone}
        @mouseenter=${() => this.hoverCard(card)} @mouseleave=${() => { this.hovered = null; }}>
        <span class="n">${line.count}</span>
        <span class="nm">${card.name}</span>
        ${this.renderFlag(flag)}
        ${editable && deck.format === "Commander" && zone === "main" && canLead(card) ? html`<button class="step" title="Make commander"
          aria-label="Make ${card.name} a commander" @click=${() => this.edit(withCommander(this.lines, card))}>★</button>` : nothing}
        ${this.renderCost(card.mana_cost)}
        ${editable ? html`<span class="steps">
          <button class="step" aria-label="One fewer ${card.name}" @click=${() => this.edit(withCard(this.lines, card, zone, -1))}>−</button>
          ${zone === "commander" ? nothing : html`<button class="step" aria-label="One more ${card.name}"
            @click=${() => this.edit(withCard(this.lines, card, zone, 1))}>+</button>`}
        </span>` : nothing}
      </div>`;
  }

  private renderArt(deck: Deck): TemplateResult {
    const flags = flagsByName(deck.checks);
    const sideboard = inZone(this.lines, "sideboard").sort(byName);
    return html`
      <div class="board" aria-label="Deck art by mana value">
        ${artColumns(this.lines).map((column) => html`
          <div class="colm" data-column=${column.label}>
            <div class="colm-head"><b>${column.label}</b><span>(${column.lines.reduce((n, l) => n + l.count, 0)})</span></div>
            <div class="stack">${column.lines.map((l) => this.renderTile(deck, l, flags.get(l.card.name)))}</div>
          </div>`)}
      </div>
      ${sideboard.length > 0 ? html`
        <div class="art-side group">
          <h5>Sideboard (${countIn(this.lines, "sideboard")})${deck.format === "Commander" ? html`<em>Not played in Commander</em>` : nothing}</h5>
          <div class="row-tiles">${sideboard.map((l) => this.renderTile(deck, l, flags.get(l.card.name)))}</div>
        </div>` : nothing}
      ${this.renderUnknownLines(deck)}`;
  }

  private renderTile(deck: Deck, line: DeckLine, flag: CardFlag | undefined): TemplateResult {
    const { card, zone } = line;
    return html`
      <div class="tile ${flag ?? ""} ${this.focused === card.name ? "focused" : ""}" data-card=${card.name} title=${card.name}
        @mouseenter=${() => this.hoverCard(card)} @mouseleave=${() => { this.hovered = null; }}>
        ${this.renderCardFace(card)}
        <div class="nm">${card.name}</div>
        ${line.count > 1 ? html`<span class="qty">×${line.count}</span>` : nothing}
        ${flag ? html`<div class="ribbon">${flag === "unplayable" ? `Not on ${this.release}` : "Check"}</div>` : nothing}
        ${deck.starter ? nothing : html`<div class="tile-steps">
          <button class="step" aria-label="One fewer ${card.name}" @click=${() => this.edit(withCard(this.lines, card, zone, -1))}>−</button>
          <button class="step" aria-label="One more ${card.name}" @click=${() => this.edit(withCard(this.lines, card, zone, 1))}>+</button>
        </div>`}
      </div>`;
  }

  private renderSide(deck: Deck): TemplateResult {
    const commanders = inZone(this.lines, "commander").map((l) => l.card);
    const candidates = inZone(this.lines, "main").map((l) => l.card).filter(canLead);
    const partnerOpen = commanders.length === 1 && commanders[0]!.can_pair;
    const result = verdict(deck.checks, deck.unknown_lines, this.rules, this.release);
    // What's wrong up front; what the deck keeps folded away.
    const kept = deck.checks.filter((c) => c.problems.length === 0);
    const failing = deck.checks.filter((c) => c.problems.length > 0);
    return html`
      ${deck.format === "Commander" ? html`
        <h4>${commanders.length > 1 ? "Commanders" : "Commander"}</h4>
        <div class="slot" aria-label="Command zone">
          ${commanders.map((c) => html`
            <div class="cmdr" data-card=${c.name} @mouseenter=${() => this.hoverCard(c)} @mouseleave=${() => { this.hovered = null; }}>
              ${this.renderCardFace(c)}
              ${deck.starter ? nothing : html`<button class="action" aria-label="Move ${c.name} back to the main deck"
                @click=${() => this.edit(withoutCommander(this.lines, c))}>↩ to the main deck</button>`}
            </div>`)}
          ${commanders.length === 0 ? html`<div class="open">No commander yet.<br>Press ★ on a card that can lead a deck.</div>` : nothing}
          ${partnerOpen ? html`<div class="open">Partner slot open.<br>Add a second commander that pairs with ${commanders[0]!.name}.</div>` : nothing}
        </div>
        ${!deck.starter && commanders.length === 0 && candidates.length > 0 ? html`
          <div class="suggest">
            <span class="seg-label">Cards in this deck that can lead it:</span>
            ${candidates.map((c) => html`<button class="btn small" @click=${() => this.edit(withCommander(this.lines, c))}>★ ${c.name}</button>`)}
          </div>` : nothing}
      ` : nothing}

      <h4>Checks</h4>
      <div class="rules-pick">
        <span class="seg-label">Check as a</span>
        <span class="seg" role="group" aria-label="Room rules"
          title="A Casual room warns about a broken format rule but lets the deck play; a Strict room refuses it. Being playable on ${this.release} is always enforced.">
          ${(["Casual", "Strict"] as const).map((r) => html`
            <button aria-pressed=${this.rules === r ? "true" : "false"} @click=${() => { this.rules = r; }}>${r}</button>`)}
        </span>
        <span class="seg-label">room</span>
      </div>
      <div class="verdict ${result.level}" role="status" aria-label="Verdict">${result.headline}</div>
      <ul class="checks" aria-label="Checklist">${failing.map((c) => this.renderCheck(c))}</ul>
      ${kept.length > 0 ? html`<details class="kept-checks">
        <summary>${kept.length} ${kept.length === 1 ? "check passes" : "checks pass"}</summary>
        <ul class="checks">${kept.map((c) => this.renderCheck(c))}</ul>
      </details>` : nothing}`;
  }

  private renderCheck(check: Check): TemplateResult {
    const kept = check.problems.length === 0;
    const refused = check.always_enforced || this.rules === "Strict";
    const status = kept ? "kept" : refused ? "refused" : "warned";
    const mark = kept ? "✓" : check.always_enforced ? "⛔" : refused ? "✗" : "!";
    return html`
      <li class="check ${status}" data-status=${status}>
        <span class="mark" aria-hidden="true">${mark}</span>
        <span class="label">${check.label}</span>
        ${kept ? nothing : html`<span class="detail">${check.problems.join("; ")}</span>`}
        ${!kept && check.cards.length > 0 ? html`<div class="cards">
          ${check.cards.slice(0, 12).map((name) => html`<button @click=${() => this.focusCard(name)}>${name}</button>`)}
          ${check.cards.length > 12 ? html`<span class="seg-label">and ${check.cards.length - 12} more, marked in the deck</span>` : nothing}
        </div>` : nothing}
      </li>`;
  }

  private hoverCard(card: Card): void {
    if (card !== this.hovered) playSound("card-hover");
    this.hovered = card;
  }

  private renderPreview(card: Card): TemplateResult {
    return html`
      <div class="preview" aria-hidden="true">
        ${this.renderCardFace(card)}
        <div class="preview-text">
          <div class="preview-name"><span>${card.name}</span>${this.renderCost(card.mana_cost)}</div>
          <div class="preview-type">${card.type_line}</div>
          ${card.oracle_text ? html`<div class="preview-oracle">${card.oracle_text}</div>` : nothing}
          ${card.playable ? nothing : html`<div class="preview-warning">Not playable on ${this.release} yet: this release has no card file for it.</div>`}
          ${card.legality === "Legal" ? nothing : html`<div class="preview-warning">${card.legality === "NotLegal" ? "Not legal" : card.legality} in ${this.deck?.format ?? "this format"}.</div>`}
        </div>
      </div>`;
  }

  private renderImport(): TemplateResult {
    return html`
      <div class="scrim" @click=${(e: Event) => { if (e.target === e.currentTarget) this.dialog = null; }}>
        <div class="dialog" role="dialog" aria-label="Import a deck" @keydown=${(e: KeyboardEvent) => { if (e.key === "Escape") this.dialog = null; }}>
          <h3>Import a deck</h3>
          <p>Either becomes a new deck of yours. From a public Archidekt deck's link, named as it is there:</p>
          <div class="row-actions">
            <input type="url" class="grow" aria-label="Archidekt link" placeholder="https://archidekt.com/decks/…" .value=${this.importLink}
              @input=${(e: Event) => { this.importLink = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === "Enter" && this.importLink.trim() !== "" && !this.importing) this.importArchidektDeck(); }}>
            <button class="btn" ?disabled=${this.importing || this.importLink.trim() === ""} @click=${() => this.importArchidektDeck()}>Import from Archidekt</button>
          </div>
          <p>Or as .deck text: one "&lt;count&gt; &lt;card name&gt;" a line, with COMMANDER:, MAINBOARD: and SIDEBOARD: headers.</p>
          <div class="row-actions">
            <input type="text" class="grow" aria-label="New deck's name" maxlength="60" .value=${this.importName}
              @input=${(e: Event) => { this.importName = (e.target as HTMLInputElement).value; }}>
            <label class="btn small">Load a file…<input type="file" accept=".deck,.txt" hidden @change=${(e: Event) => this.loadImportFile(e)}></label>
          </div>
          <textarea aria-label=".deck text" spellcheck="false" placeholder="COMMANDER:&#10;1 Winota, Joiner of Forces&#10;&#10;MAINBOARD:&#10;1 Lightning Bolt"
            .value=${this.importText} @input=${(e: Event) => { this.importText = (e.target as HTMLTextAreaElement).value; }}></textarea>
          ${this.importError ? html`<div class="errors" role="alert">
            ${this.importUnknown.length > 0
              ? html`<div>No card is named:</div><ul>${this.importUnknown.map((n) => html`<li>${n}</li>`)}</ul>`
              : html`<div>${this.importError}</div>`}
          </div>` : nothing}
          <div class="row-actions">
            <span class="grow"></span>
            <button class="btn" @click=${() => { this.dialog = null; }}>Close</button>
            <button class="btn primary" ?disabled=${this.importing} @click=${() => this.importDeckText()}>Import as a new deck</button>
          </div>
        </div>
      </div>`;
  }

  private renderExport(deck: Deck): TemplateResult {
    return html`
      <div class="scrim" @click=${(e: Event) => { if (e.target === e.currentTarget) this.dialog = null; }}>
        <div class="dialog" role="dialog" aria-label="Export ${deck.name}" @keydown=${(e: KeyboardEvent) => { if (e.key === "Escape") this.dialog = null; }}>
          <h3>Export ${deck.name}</h3>
          <p>The deck as .deck text, its cards by name. Importing it makes the same deck again.</p>
          <textarea aria-label=".deck text" readonly spellcheck="false" .value=${this.exportText}></textarea>
          <div class="row-actions">
            <span class="grow"></span>
            <button class="btn" @click=${() => { this.dialog = null; }}>Close</button>
            <button class="btn" @click=${() => navigator.clipboard?.writeText(this.exportText)}>Copy text</button>
            <button class="btn primary" ?disabled=${this.exportText === ""} @click=${() => this.downloadExport()}>Download .deck</button>
          </div>
        </div>
      </div>`;
  }

  // The card whose art the deck's banner shows: its commander, or else its first creature with art.
  private bannerCard(): Card | undefined {
    return inZone(this.lines, "commander")[0]?.card
      ?? inZone(this.lines, "main").sort(byName).find((l) => l.card.playable && typeGroup(l.card) === "Creature")?.card;
  }

  // Art for the registry's cards, which art-sync uploads; a text card for the rest.
  private renderCardFace(card: Card): TemplateResult {
    return html`<card-art name=${card.name} ?artless=${!card.playable} frame=${frameOfManaCost(card.mana_cost)} manaCost=${card.mana_cost} typeLine=${card.type_line}
      oracleText=${card.oracle_text}></card-art>`;
  }

  private renderCost(cost: string): TemplateResult {
    return html`<span class="cost">${costSymbols(cost).map((s) => html`<span class="pip ${pipColor(s)}">${s.replace("/P", "").replace("/", "")}</span>`)}</span>`;
  }

  private renderIdentity(colors: string[]): TemplateResult {
    if (colors.length === 0) return html`<span class="cost" title="Colorless"><span class="pip C">C</span></span>`;
    return html`<span class="cost" title="Color identity">${colors.map((c) => html`<span class="pip ${c}">${c}</span>`)}</span>`;
  }

  private renderFlag(flag: CardFlag | undefined): TemplateResult | typeof nothing {
    if (!flag) return nothing;
    if (flag === "unplayable") return html`<span class="flag unplayable">Not on ${this.release}</span>`;
    return html`<span class="flag rule ${this.rules === "Strict" ? "strict" : ""}">${this.rules === "Strict" ? "Refused" : "Warning"}</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deck-editor": DeckEditor;
  }
}
